import React, { useState } from 'react';
import axios from 'axios';
import { Send, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    inquiry_type: 'general'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Message sent successfully! We\'ll get back to you soon.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        message: '',
        inquiry_type: 'general'
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-6">
      <div className="grid sm:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Full Name *
          </label>
          <Input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            data-testid="contact-name"
            placeholder="Your full name"
            className="bg-white border-orange-200 focus:border-coral focus:ring-coral/20 rounded-xl h-12"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Email Address *
          </label>
          <Input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            data-testid="contact-email"
            placeholder="you@example.com"
            className="bg-white border-orange-200 focus:border-coral focus:ring-coral/20 rounded-xl h-12"
          />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Phone Number *
          </label>
          <Input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            required
            data-testid="contact-phone"
            placeholder="+27 XX XXX XXXX"
            className="bg-white border-orange-200 focus:border-coral focus:ring-coral/20 rounded-xl h-12"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Inquiry Type
          </label>
          <select
            name="inquiry_type"
            value={formData.inquiry_type}
            onChange={handleChange}
            data-testid="contact-inquiry-type"
            className="w-full bg-white border border-orange-200 focus:border-coral focus:ring-2 focus:ring-coral/20 rounded-xl h-12 px-4 text-slate-900"
          >
            <option value="general">General Inquiry</option>
            <option value="application">Application</option>
            <option value="viewing">Book a Viewing</option>
            <option value="nsfas">NSFAS Query</option>
            <option value="maintenance">Maintenance</option>
          </select>
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Message *
        </label>
        <Textarea
          name="message"
          value={formData.message}
          onChange={handleChange}
          required
          data-testid="contact-message"
          placeholder="How can we help you?"
          rows={5}
          className="bg-white border-orange-200 focus:border-coral focus:ring-coral/20 rounded-xl resize-none"
        />
      </div>
      <Button
        type="submit"
        disabled={isSubmitting}
        data-testid="contact-submit-btn"
        className="w-full bg-gradient-to-r from-coral to-orange hover:from-coral-dark hover:to-coral text-white rounded-full py-6 text-lg font-semibold disabled:opacity-50 shadow-lg shadow-coral/25"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            Sending...
          </>
        ) : (
          <>
            <Send className="w-5 h-5 mr-2" />
            Send Message
          </>
        )}
      </Button>
    </form>
  );
};
